import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { RootState } from '../app/store';
import {
    ApiEnvelope,
    LoginRequest,
    SignupRequest,
    LogoRow,
    AdminRequestCount,
    Training,
    TrainingPhoto,
    AdminRequest,
    ClubMember,
    Race,
    RaceDetail,
    FeedTraining,
    FeedEvent,
    FeedRace,
    RaceRequest,
    RaceRequestCount,
    HistoryResponse,
    EventItem,
    MyProfile,
    UpdateProfileRequest,
    ChangePasswordRequest,
    ClubListItem,
    ClubSettingsInfo,
    ClubInvite,
    MyClub,
    RegisterClubRequest,
    SuperAdminClubRow,
    ContactAdminsResult,
    ClubBillingInfo,
    BillingTransaction,
    WeatherForecast,
    LocationSuggestion,
} from '../types/api';

export const BASE_URL = process.env.REACT_APP_API_URL || 'https://tivity.fr:4000';

export const apiSlice = createApi({
    reducerPath: 'api',
    baseQuery: fetchBaseQuery({
        baseUrl: BASE_URL,
        prepareHeaders: (headers, { getState }) => {
            const token = (getState() as RootState).auth.token;

            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            }

            return headers;
        },
    }),
    tagTypes: ['Training', 'TrainingPhoto', 'AdminRequest', 'ClubMember', 'Race', 'ClubLogo', 'RaceRequest', 'Event', 'Profile', 'ClubDirectory', 'ClubSettings', 'MyClubs', 'SuperAdminClubs', 'ClubBilling'],
    endpoints: (builder) => ({
        getLogoPath: builder.query<LogoRow[], string>({
            query: (clubID) => `/api/getLogoPath/${clubID}`,
            transformResponse: (response: ApiEnvelope<LogoRow[]>) => response.message,
            providesTags: ['ClubLogo'],
        }),

        updateClubLogo: builder.mutation<string, { clubId: number; formData: FormData }>({
            query: ({ clubId, formData }) => ({ url: `/api/updateClubLogo/${clubId}`, method: 'PUT', body: formData }),
            transformResponse: (response: ApiEnvelope<string>) => response.message,
            invalidatesTags: ['ClubLogo', 'ClubSettings'],
        }),

        getClubs: builder.query<ClubListItem[], void>({
            query: () => '/api/clubs',
            transformResponse: (response: ApiEnvelope<ClubListItem[]>) => response.message,
            providesTags: ['ClubDirectory'],
        }),

        getClubSettings: builder.query<ClubSettingsInfo, string>({
            query: (clubId) => `/api/getClubSettings/${clubId}`,
            transformResponse: (response: ApiEnvelope<ClubSettingsInfo>) => response.message,
            providesTags: ['ClubSettings'],
        }),

        updateClubVisibility: builder.mutation<string, { clubId: number; isPublic: boolean }>({
            query: ({ clubId, isPublic }) => ({ url: `/api/updateClubVisibility/${clubId}`, method: 'PUT', body: { isPublic } }),
            transformResponse: (response: ApiEnvelope<string>) => response.message,
            invalidatesTags: ['ClubSettings', 'ClubDirectory'],
        }),

        updateClubBrief: builder.mutation<string, { clubId: number; brief: string }>({
            query: ({ clubId, brief }) => ({ url: `/api/updateClubBrief/${clubId}`, method: 'PUT', body: { brief } }),
            transformResponse: (response: ApiEnvelope<string>) => response.message,
            invalidatesTags: ['ClubSettings', 'ClubDirectory'],
        }),

        createClubInvite: builder.mutation<ClubInvite, { clubId: number; email: string }>({
            query: ({ clubId, email }) => ({ url: `/api/clubs/${clubId}/invites`, method: 'POST', body: { email } }),
            transformResponse: (response: ApiEnvelope<ClubInvite>) => response.message,
        }),

        login: builder.mutation<string, LoginRequest>({
            query: (body) => ({ url: '/api/login', method: 'POST', body }),
            transformResponse: (response: ApiEnvelope<string>) => response.message,
        }),

        signup: builder.mutation<string, SignupRequest>({
            query: (body) => ({ url: '/api/signup', method: 'POST', body }),
            transformResponse: (response: ApiEnvelope<string>) => response.message,
        }),

        registerClub: builder.mutation<string, RegisterClubRequest>({
            query: (body) => ({ url: '/api/registerClub', method: 'POST', body }),
            transformResponse: (response: ApiEnvelope<string>) => response.message,
        }),

        loginWithFirebase: builder.mutation<string, { idToken: string; clubId?: number; invite?: string }>({
            query: (body) => ({ url: '/api/auth/firebase', method: 'POST', body }),
            transformResponse: (response: ApiEnvelope<string>) => response.message,
        }),

        getMyClubs: builder.query<MyClub[], void>({
            query: () => '/api/myClubs',
            transformResponse: (response: ApiEnvelope<MyClub[]>) => response.message,
            providesTags: ['MyClubs'],
        }),

        switchClub: builder.mutation<string, number>({
            query: (clubId) => ({ url: `/api/switchClub/${clubId}`, method: 'POST' }),
            transformResponse: (response: ApiEnvelope<string>) => response.message,
        }),

        joinClub: builder.mutation<string, { clubId: number; invite?: string }>({
            query: ({ clubId, invite }) => ({ url: `/api/joinClub/${clubId}`, method: 'POST', body: { invite } }),
            transformResponse: (response: ApiEnvelope<string>) => response.message,
            invalidatesTags: ['MyClubs'],
        }),

        getNbAdminRequest: builder.query<AdminRequestCount[], string>({
            query: (clubID) => `/api/getNbAdminRequest/${clubID}`,
            transformResponse: (response: ApiEnvelope<AdminRequestCount[]>) => response.message,
            providesTags: ['AdminRequest'],
        }),

        getTraining: builder.query<Training[], number>({
            query: (userID) => ({ url: `/api/getTraining/${userID}`, method: 'POST' }),
            transformResponse: (response: ApiEnvelope<Training[]>) => response.message,
            providesTags: ['Training'],
        }),

        // Cross-club feeds: outings / events / races aggregated over every
        // club the caller is an approved member of. Tagged the same as the
        // per-club queries so the existing subscribe/RSVP mutations refresh
        // them without extra invalidation wiring.
        getFeedOutings: builder.query<FeedTraining[], void>({
            query: () => '/api/feed/outings',
            transformResponse: (response: ApiEnvelope<FeedTraining[]>) => response.message,
            providesTags: ['Training'],
        }),

        getFeedEvents: builder.query<FeedEvent[], void>({
            query: () => '/api/feed/events',
            transformResponse: (response: ApiEnvelope<FeedEvent[]>) => response.message,
            providesTags: ['Event'],
        }),

        getFeedRaces: builder.query<FeedRace[], void>({
            query: () => '/api/feed/races',
            transformResponse: (response: ApiEnvelope<FeedRace[]>) => response.message,
            providesTags: ['Race'],
        }),

        subscribeTraining: builder.mutation<string, { userID: number; trainingID: number }>({
            query: ({ userID, trainingID }) => ({ url: `/api/subscribeTraining/${userID}/${trainingID}`, method: 'POST' }),
            transformResponse: (response: ApiEnvelope<string>) => response.message,
            invalidatesTags: ['Training'],
        }),

        unsubscribeTraining: builder.mutation<string, { userID: number; trainingID: number }>({
            query: ({ userID, trainingID }) => ({ url: `/api/unsubscribeTraining/${userID}/${trainingID}`, method: 'DELETE' }),
            transformResponse: (response: ApiEnvelope<string>) => response.message,
            invalidatesTags: ['Training'],
        }),

        addTraining: builder.mutation<string, FormData>({
            query: (formData) => ({ url: '/api/addTraining', method: 'POST', body: formData }),
            transformResponse: (response: ApiEnvelope<string>) => response.message,
            invalidatesTags: ['Training'],
        }),

        updateTraining: builder.mutation<string, { trainingId: number; formData: FormData }>({
            query: ({ trainingId, formData }) => ({ url: `/api/updateTraining/${trainingId}`, method: 'PUT', body: formData }),
            transformResponse: (response: ApiEnvelope<string>) => response.message,
            invalidatesTags: ['Training'],
        }),

        deleteTraining: builder.mutation<string, number>({
            query: (trainingId) => ({ url: `/api/deleteTraining/${trainingId}`, method: 'DELETE' }),
            transformResponse: (response: ApiEnvelope<string>) => response.message,
            invalidatesTags: ['Training'],
        }),

        getWeather: builder.query<WeatherForecast, { lat: string; lon: string; date: string }>({
            query: ({ lat, lon, date }) => `/api/weather?lat=${lat}&lon=${lon}&date=${date}`,
            transformResponse: (response: ApiEnvelope<WeatherForecast>) => response.message,
        }),

        searchLocations: builder.query<LocationSuggestion[], { q: string; lang: string }>({
            query: ({ q, lang }) => `/api/geo/search?q=${encodeURIComponent(q)}&lang=${lang}`,
            transformResponse: (response: ApiEnvelope<LocationSuggestion[]>) => response.message,
            keepUnusedDataFor: 300,
        }),

        getTrainingPhotos: builder.query<TrainingPhoto[], number>({
            query: (trainingID) => `/api/trainings/${trainingID}/photos`,
            transformResponse: (response: ApiEnvelope<TrainingPhoto[]>) => response.message,
            providesTags: ['TrainingPhoto'],
        }),

        uploadTrainingPhoto: builder.mutation<string, { trainingID: number; formData: FormData }>({
            query: ({ trainingID, formData }) => ({ url: `/api/trainings/${trainingID}/photos`, method: 'POST', body: formData }),
            transformResponse: (response: ApiEnvelope<string>) => response.message,
            invalidatesTags: ['TrainingPhoto'],
        }),

        deleteTrainingPhoto: builder.mutation<string, { trainingID: number; photoID: number }>({
            query: ({ trainingID, photoID }) => ({ url: `/api/trainings/${trainingID}/photos/${photoID}`, method: 'DELETE' }),
            transformResponse: (response: ApiEnvelope<string>) => response.message,
            invalidatesTags: ['TrainingPhoto'],
        }),

        getHistory: builder.query<HistoryResponse, void>({
            query: () => '/api/feed/history',
            transformResponse: (response: ApiEnvelope<HistoryResponse>) => response.message,
            providesTags: ['Training', 'Event'],
        }),

        getUserRequests: builder.query<AdminRequest[], string>({
            query: (clubID) => `/api/getUserRequests/${clubID}`,
            transformResponse: (response: ApiEnvelope<AdminRequest[]>) => response.message,
            providesTags: ['AdminRequest'],
        }),

        acceptUser: builder.mutation<string, { userID: number; adminID: number }>({
            query: ({ userID, adminID }) => ({ url: `/api/acceptUser/${userID}/${adminID}`, method: 'POST' }),
            transformResponse: (response: ApiEnvelope<string>) => response.message,
            invalidatesTags: ['AdminRequest'],
        }),

        rejectUser: builder.mutation<string, number>({
            query: (userID) => ({ url: `/api/rejectUser/${userID}`, method: 'POST' }),
            transformResponse: (response: ApiEnvelope<string>) => response.message,
            invalidatesTags: ['AdminRequest'],
        }),

        getClubMembers: builder.query<ClubMember[], string>({
            query: (clubID) => `/api/getClubMembers/${clubID}`,
            transformResponse: (response: ApiEnvelope<ClubMember[]>) => response.message,
            providesTags: ['ClubMember'],
        }),

        removeUser: builder.mutation<string, number>({
            query: (userID) => ({ url: `/api/removeUser/${userID}`, method: 'DELETE' }),
            transformResponse: (response: ApiEnvelope<string>) => response.message,
            invalidatesTags: ['ClubMember'],
        }),

        updateMemberRole: builder.mutation<string, { userId: number; role: 'USER' | 'ANIMATOR' | 'ADMIN' }>({
            query: ({ userId, role }) => ({ url: `/api/updateMemberRole/${userId}`, method: 'PUT', body: { role } }),
            transformResponse: (response: ApiEnvelope<string>) => response.message,
            invalidatesTags: ['ClubMember'],
        }),

        getRaces: builder.query<Race[], string>({
            query: (clubID) => `/api/getRaces/${clubID}`,
            transformResponse: (response: ApiEnvelope<Race[]>) => response.message,
            providesTags: ['Race'],
        }),

        getRace: builder.query<RaceDetail, number>({
            query: (raceId) => `/api/getRace/${raceId}`,
            transformResponse: (response: ApiEnvelope<RaceDetail>) => response.message,
            providesTags: ['Race'],
        }),

        addRace: builder.mutation<string, FormData>({
            query: (formData) => ({ url: '/api/addRace', method: 'POST', body: formData }),
            transformResponse: (response: ApiEnvelope<string>) => response.message,
            invalidatesTags: ['Race'],
        }),

        updateRace: builder.mutation<string, { raceId: number; formData: FormData }>({
            query: ({ raceId, formData }) => ({ url: `/api/updateRace/${raceId}`, method: 'PUT', body: formData }),
            transformResponse: (response: ApiEnvelope<string>) => response.message,
            invalidatesTags: ['Race'],
        }),

        deleteRace: builder.mutation<string, number>({
            query: (raceId) => ({ url: `/api/deleteRace/${raceId}`, method: 'DELETE' }),
            transformResponse: (response: ApiEnvelope<string>) => response.message,
            invalidatesTags: ['Race'],
        }),

        getPendingRaces: builder.query<RaceRequest[], string>({
            query: (clubID) => `/api/getPendingRaces/${clubID}`,
            transformResponse: (response: ApiEnvelope<RaceRequest[]>) => response.message,
            providesTags: ['RaceRequest'],
        }),

        getNbPendingRaces: builder.query<RaceRequestCount[], string>({
            query: (clubID) => `/api/getNbPendingRaces/${clubID}`,
            transformResponse: (response: ApiEnvelope<RaceRequestCount[]>) => response.message,
            providesTags: ['RaceRequest'],
        }),

        approveRace: builder.mutation<string, number>({
            query: (raceId) => ({ url: `/api/approveRace/${raceId}`, method: 'POST' }),
            transformResponse: (response: ApiEnvelope<string>) => response.message,
            invalidatesTags: ['Race', 'RaceRequest'],
        }),

        rejectRace: builder.mutation<string, number>({
            query: (raceId) => ({ url: `/api/rejectRace/${raceId}`, method: 'POST' }),
            transformResponse: (response: ApiEnvelope<string>) => response.message,
            invalidatesTags: ['Race', 'RaceRequest'],
        }),

        getEvents: builder.query<EventItem[], number>({
            query: (userID) => `/api/getEvents/${userID}`,
            transformResponse: (response: ApiEnvelope<EventItem[]>) => response.message,
            providesTags: ['Event'],
        }),

        addEvent: builder.mutation<string, FormData>({
            query: (formData) => ({ url: '/api/addEvent', method: 'POST', body: formData }),
            transformResponse: (response: ApiEnvelope<string>) => response.message,
            invalidatesTags: ['Event'],
        }),

        updateEvent: builder.mutation<string, { eventId: number; formData: FormData }>({
            query: ({ eventId, formData }) => ({ url: `/api/updateEvent/${eventId}`, method: 'PUT', body: formData }),
            transformResponse: (response: ApiEnvelope<string>) => response.message,
            invalidatesTags: ['Event'],
        }),

        deleteEvent: builder.mutation<string, number>({
            query: (eventId) => ({ url: `/api/deleteEvent/${eventId}`, method: 'DELETE' }),
            transformResponse: (response: ApiEnvelope<string>) => response.message,
            invalidatesTags: ['Event'],
        }),

        subscribeEvent: builder.mutation<string, { userID: number; eventID: number }>({
            query: ({ userID, eventID }) => ({ url: `/api/subscribeEvent/${userID}/${eventID}`, method: 'POST' }),
            transformResponse: (response: ApiEnvelope<string>) => response.message,
            invalidatesTags: ['Event'],
        }),

        unsubscribeEvent: builder.mutation<string, { userID: number; eventID: number }>({
            query: ({ userID, eventID }) => ({ url: `/api/unsubscribeEvent/${userID}/${eventID}`, method: 'POST' }),
            transformResponse: (response: ApiEnvelope<string>) => response.message,
            invalidatesTags: ['Event'],
        }),

        getMyProfile: builder.query<MyProfile, void>({
            query: () => '/api/getMyProfile',
            transformResponse: (response: ApiEnvelope<MyProfile>) => response.message,
            providesTags: ['Profile'],
        }),

        updateMyProfile: builder.mutation<string, UpdateProfileRequest>({
            query: (body) => ({ url: '/api/updateMyProfile', method: 'PUT', body }),
            transformResponse: (response: ApiEnvelope<string>) => response.message,
            invalidatesTags: ['Profile', 'ClubMember'],
        }),

        changePassword: builder.mutation<string, ChangePasswordRequest>({
            query: (body) => ({ url: '/api/changePassword', method: 'PUT', body }),
            transformResponse: (response: ApiEnvelope<string>) => response.message,
        }),

        getSuperAdminClubs: builder.query<SuperAdminClubRow[], void>({
            query: () => '/api/superadmin/clubs',
            transformResponse: (response: ApiEnvelope<SuperAdminClubRow[]>) => response.message,
            providesTags: ['SuperAdminClubs'],
        }),

        getSuperAdminTransactions: builder.query<BillingTransaction[], void>({
            query: () => '/api/superadmin/transactions',
            transformResponse: (response: ApiEnvelope<BillingTransaction[]>) => response.message,
        }),

        impersonateClub: builder.mutation<string, number>({
            query: (clubId) => ({ url: `/api/superadmin/impersonate/${clubId}`, method: 'POST' }),
            transformResponse: (response: ApiEnvelope<string>) => response.message,
        }),

        contactClubAdmins: builder.mutation<ContactAdminsResult, { clubId: number; message: string }>({
            query: ({ clubId, message }) => ({ url: `/api/superadmin/contact-admin/${clubId}`, method: 'POST', body: { message } }),
            transformResponse: (response: ApiEnvelope<ContactAdminsResult>) => response.message,
        }),

        getClubBilling: builder.query<ClubBillingInfo, number>({
            query: (clubId) => `/api/clubs/${clubId}/billing`,
            transformResponse: (response: ApiEnvelope<ClubBillingInfo>) => response.message,
            providesTags: ['ClubBilling'],
        }),

        startBillingCheckout: builder.mutation<string, { clubId: number; plan: 'club' | 'league' }>({
            query: ({ clubId, plan }) => ({ url: `/api/clubs/${clubId}/billing/checkout`, method: 'POST', body: { plan } }),
            transformResponse: (response: ApiEnvelope<string>) => response.message,
            invalidatesTags: ['ClubBilling'],
        }),
    }),
});

export const {
    useGetLogoPathQuery,
    useLoginMutation,
    useSignupMutation,
    useRegisterClubMutation,
    useLoginWithFirebaseMutation,
    useGetMyClubsQuery,
    useSwitchClubMutation,
    useJoinClubMutation,
    useGetClubsQuery,
    useGetClubSettingsQuery,
    useUpdateClubVisibilityMutation,
    useUpdateClubBriefMutation,
    useCreateClubInviteMutation,
    useGetNbAdminRequestQuery,
    useGetTrainingQuery,
    useGetFeedOutingsQuery,
    useGetFeedEventsQuery,
    useGetFeedRacesQuery,
    useSubscribeTrainingMutation,
    useUnsubscribeTrainingMutation,
    useAddTrainingMutation,
    useUpdateTrainingMutation,
    useDeleteTrainingMutation,
    useGetWeatherQuery,
    useSearchLocationsQuery,
    useGetTrainingPhotosQuery,
    useUploadTrainingPhotoMutation,
    useDeleteTrainingPhotoMutation,
    useGetUserRequestsQuery,
    useAcceptUserMutation,
    useRejectUserMutation,
    useGetClubMembersQuery,
    useRemoveUserMutation,
    useUpdateMemberRoleMutation,
    useUpdateClubLogoMutation,
    useGetRacesQuery,
    useGetRaceQuery,
    useAddRaceMutation,
    useUpdateRaceMutation,
    useDeleteRaceMutation,
    useGetPendingRacesQuery,
    useGetNbPendingRacesQuery,
    useApproveRaceMutation,
    useRejectRaceMutation,
    useGetHistoryQuery,
    useGetEventsQuery,
    useAddEventMutation,
    useUpdateEventMutation,
    useDeleteEventMutation,
    useSubscribeEventMutation,
    useUnsubscribeEventMutation,
    useGetMyProfileQuery,
    useUpdateMyProfileMutation,
    useChangePasswordMutation,
    useGetSuperAdminClubsQuery,
    useGetSuperAdminTransactionsQuery,
    useImpersonateClubMutation,
    useContactClubAdminsMutation,
    useGetClubBillingQuery,
    useStartBillingCheckoutMutation,
} = apiSlice;
